import { useQuery, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/layout/PageHeader';
import Table from '../../components/ui/Table';
import Badge from '../../components/ui/Badge';
import Spinner from '../../components/ui/Spinner';
import EmptyState from '../../components/ui/EmptyState';
import RequestActions, { CandidateCell, ReviewedCell, fmtDate, selectColumn } from '../../components/requests/RequestActions';
import { listStepApprovalRequests, approveStepApprovalRequest, rejectStepApprovalRequest } from '../../api/candidates';
import RequestFilters, { StatusBadge, useRequestFilters } from '../../components/requests/RequestFilters';
import { BulkActionBar, useRowSelection } from '../../components/requests/BulkSelection';

export const STEP_APPROVAL_REQUESTS_QUERY_KEY = ['step-approval-requests'];
export const STEP_APPROVAL_REQUESTS_COUNT_QUERY_KEY = ['step-approval-requests', 'count'];

const columnsFor = (selection, refetch) => [
  selectColumn(selection, (r) => r.id, (r) => r.status === 'pending'),
  { key: 'candidate', label: 'Candidate', render: (row) => <CandidateCell candidate={row.candidate} /> },
  {
    key: 'step',
    label: 'Step',
    render: (row) => (
      <div>
        {row.step.title}
        {row.step.remarks && <div className="text-xs text-gray-400">{row.step.remarks}</div>}
      </div>
    ),
  },
  { key: 'proposed_action', label: 'Action', render: (row) => <span className="capitalize">{row.proposed_action}</span> },
  { key: 'level', label: 'Level', render: (row) => <Badge tone="amber">Level {row.level}</Badge> },
  {
    key: 'requested',
    label: 'Requested',
    render: (row) => (
      <div>
        {row.requested_by}
        <div className="text-xs text-gray-400">{fmtDate(row.requested_at)}</div>
        {row.request_note && <div className="text-xs italic text-gray-500">"{row.request_note}"</div>}
      </div>
    ),
  },
  {
    key: 'status',
    label: 'Status',
    render: (row) => (
      <div>
        <StatusBadge status={row.status} />
        <ReviewedCell row={row} />
      </div>
    ),
  },
  {
    key: 'actions',
    label: '',
    render: (row) => (
      <RequestActions
        pending={row.status === 'pending'}
        onApprove={() => approveStepApprovalRequest(row.id)}
        onReject={(reason) => rejectStepApprovalRequest(row.id, reason)}
        onResolved={refetch}
        approvedMessage="Approval granted — step completed and candidate advanced."
        rejectedMessage="Approval rejected."
      />
    ),
  },
];

const searchFields = (row) => [
  row.candidate.full_name,
  row.candidate.reference_no,
  row.candidate.contact_no,
  row.candidate.nationality,
  row.candidate.category,
  row.step.title,
  row.requested_by,
];
const requestedAt = (row) => row.requested_at;

export default function StepApprovalRequests() {
  const queryClient = useQueryClient();
  const { filters, set, apply, reset, dirty, candidate } = useRequestFilters(searchFields, requestedAt);
  const query = useQuery({
    queryKey: [...STEP_APPROVAL_REQUESTS_QUERY_KEY, filters.status, candidate.params],
    queryFn: () => listStepApprovalRequests({ ...candidate.params, status: filters.status }),
  });
  const allRows = query.data || [];
  const rows = apply(allRows);
  const selection = useRowSelection(rows, (r) => r.id, (r) => r.status === 'pending');

  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: STEP_APPROVAL_REQUESTS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: STEP_APPROVAL_REQUESTS_COUNT_QUERY_KEY });
  };

  return (
    <div>
      <PageHeader
        title="Approval Requests"
        description="Workflow steps configured to require sign-off — nothing advances for the candidate until approved or rejected here."
      />

      <RequestFilters candidate={candidate} filters={filters} set={set} reset={reset} dirty={dirty} total={allRows.length} shown={rows.length} />

      <BulkActionBar
        selection={selection}
        onApprove={approveStepApprovalRequest}
        onReject={rejectStepApprovalRequest}
        onDone={refetch}
      />

      {query.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState title={dirty ? 'No matching requests' : 'Nothing pending'} message={dirty ? 'No requests match the current filters.' : 'No step approval requests are currently awaiting review.'} />
      ) : (
        <Table columns={columnsFor(selection, refetch)} data={rows} />
      )}
    </div>
  );
}
